import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  UserManager,
  User,
  UserManagerSettings,
  WebStorageStateStore
} from 'oidc-client-ts';

// Environment Detection
const isDevelopment = process.env.NODE_ENV === 'development';
const isLocalhost = window.location.hostname === 'localhost';

// Azure AD Configuration
const CLIENT_ID = process.env.REACT_APP_AZURE_CLIENT_ID || '';
const TENANT_ID = process.env.REACT_APP_AZURE_TENANT_ID || '';

if (!CLIENT_ID || !TENANT_ID) {
  throw new Error('Azure AD Client ID und Tenant ID sind erforderlich');
}

// Automatische URI Detection
const getRedirectUri = (): string => {
  if (isDevelopment || isLocalhost) {
    return 'http://localhost:3000/';  // Verwende Root-URL
  }
  return window.location.origin + '/';
};

const getPostLogoutRedirectUri = (): string => {
  if (isDevelopment || isLocalhost) {
    return 'http://localhost:3000/login';
  }
  return window.location.origin + '/login';
};

// OIDC Configuration
const oidcConfig: UserManagerSettings = {
  authority: `https://login.microsoftonline.com/${TENANT_ID}/v2.0`,
  client_id: CLIENT_ID,
  redirect_uri: getRedirectUri(),
  post_logout_redirect_uri: getPostLogoutRedirectUri(),
  response_type: 'code',
  scope: `openid profile email api://${CLIENT_ID}/Read`,

  // PKCE für Sicherheit
  response_mode: 'query',  // Zurück zu query für bessere Kompatibilität

  // Silent Renewal deaktiviert für bessere Stabilität
  silent_redirect_uri: getRedirectUri(),
  automaticSilentRenew: false,  // Deaktiviert für bessere Stabilität

  // Storage
  userStore: new WebStorageStateStore({ store: window.localStorage }),

  // Metadata (für Azure AD v2.0)
  metadata: {
    issuer: `https://login.microsoftonline.com/${TENANT_ID}/v2.0`,
    authorization_endpoint: `https://login.microsoftonline.com/${TENANT_ID}/oauth2/v2.0/authorize`,
    token_endpoint: `https://login.microsoftonline.com/${TENANT_ID}/oauth2/v2.0/token`,
    userinfo_endpoint: 'https://graph.microsoft.com/oidc/userinfo',
    end_session_endpoint: `https://login.microsoftonline.com/${TENANT_ID}/oauth2/v2.0/logout`,
    jwks_uri: `https://login.microsoftonline.com/${TENANT_ID}/discovery/v2.0/keys`
  },

  // Erweiterte Konfiguration
  loadUserInfo: false, // Nutze ID Token Claims
  monitorSession: false, // Kein Session Monitoring
  revokeTokensOnSignout: false,  // Vereinfacht Logout

  // Azure AD spezifische Einstellungen
  extraQueryParams: {
    prompt: 'select_account'  // Zeigt Account-Auswahl an
  }
};

// AuthContext Interface
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: {
    email: string;
    name: string;
    firstName?: string;
    lastName?: string;
    objectId?: string;
  } | null;
  accessToken: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>; // Lokaler Logout (Standard)
  logoutFromAzure: () => Promise<void>; // Vollständiger Azure-Logout (optional)
  logoutLocal: () => Promise<void>; // Alias für logout (Kompatibilität)
}

// AuthContext
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  accessToken: null,
  login: async () => { },
  logout: async () => { },
  logoutLocal: async () => { },
  logoutFromAzure: async () => { },
});

// User Manager Instance
const userManager = new UserManager(oidcConfig);

// AuthProvider
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthContextType['user']>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Debug-Funktion
  const logOIDCConfig = () => {
    if (process.env.NODE_ENV === 'development') {
      console.group('🔐 OIDC Configuration');
      console.table({
        isDevelopment,
        isLocalhost,
        authority: oidcConfig.authority,
        client_id: oidcConfig.client_id,
        redirect_uri: oidcConfig.redirect_uri,
        post_logout_redirect_uri: oidcConfig.post_logout_redirect_uri,
        configValid: CLIENT_ID === 'aa0f5361-4420-4f3c-9a54-2915f44937da'
      });
      console.groupEnd();
    }
  };

  // Initialisierung
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // logOIDCConfig();
    }

    const initializeAuth = async () => {
      try {
        // Prüfe auf Redirect Response (sowohl Query als auch Fragment)
        const hasQueryCode = window.location.search.includes('code=');
        const hasFragmentCode = window.location.hash.includes('code=') || window.location.hash.includes('id_token=');

        if (hasQueryCode || hasFragmentCode) {
          try {
            const result = await userManager.signinRedirectCallback();
            // URL nach erfolgreichem Callback bereinigen
            window.history.replaceState({}, document.title, window.location.pathname);
          } catch (callbackError) {
            console.error('OIDC Callback-Verarbeitung fehlgeschlagen:', callbackError);
            // Trotzdem versuchen normalen User zu laden
          }
        }

        // Lade aktuellen User
        const currentUser = await userManager.getUser();

        if (currentUser && !currentUser.expired) {
          updateUserState(currentUser);
        } else {
          setIsAuthenticated(false);
          setUser(null);
          setAccessToken(null);
        }
      } catch (error) {
        console.error('OIDC Initialisierung fehlgeschlagen:', error);
        setIsAuthenticated(false);
        setUser(null);
        setAccessToken(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Event Listeners
    const handleUserLoaded = (user: User) => {
      updateUserState(user);
    };

    const handleUserUnloaded = () => {
      setIsAuthenticated(false);
      setUser(null);
      setAccessToken(null);
    };

    const handleUserSignedOut = () => {
      setIsAuthenticated(false);
      setUser(null);
      setAccessToken(null);
    };

    const handleSilentRenewError = (error: Error) => {
      console.warn('OIDC Silent Renew Fehler:', error);
      // Bei Silent Renew Fehlern könnte ein Re-Login erforderlich sein
    };

    // Event Listeners registrieren
    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);
    userManager.events.addUserSignedOut(handleUserSignedOut);
    userManager.events.addSilentRenewError(handleSilentRenewError);

    // Cleanup
    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeUserSignedOut(handleUserSignedOut);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, []);

  // Helper: User State aktualisieren
  const updateUserState = (oidcUser: User) => {
    const userData = {
      email: oidcUser.profile.email || oidcUser.profile.preferred_username || '',
      name: oidcUser.profile.name || 'Unknown User',
      firstName: oidcUser.profile.given_name,
      lastName: oidcUser.profile.family_name,
      objectId: (oidcUser.profile.oid || oidcUser.profile.sub) as string
    };

    setUser(userData);
    setAccessToken(oidcUser.access_token);
    setIsAuthenticated(true);
    setIsLoading(false);
  };

  // Login-Funktion
  const login = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await userManager.signinRedirect();
      // Nach Redirect wird diese Zeile nie erreicht
    } catch (error) {
      console.error('OIDC Login fehlgeschlagen:', error);
      setIsLoading(false);
      throw error;
    }
  };

  // Lokaler Logout (Standard - wie beim Kollegen)
  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await userManager.removeUser();
      setIsAuthenticated(false);
      setUser(null);
      setAccessToken(null);
      setIsLoading(false);
    } catch (error) {
      console.error('OIDC Lokaler Logout fehlgeschlagen:', error);
      setIsLoading(false);
      throw error;
    }
  };

  // Vollständiger Azure-Logout (optional, falls explizit gewünscht)
  const logoutFromAzure = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await userManager.signoutRedirect();
      // Nach Redirect wird diese Zeile nie erreicht
    } catch (error) {
      console.error('OIDC Azure-Logout fehlgeschlagen:', error);
      setIsLoading(false);
      throw error;
    }
  };

  // Alias für Kompatibilität (wird entfernt)
  const logoutLocal = logout;

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    accessToken,
    login,
    logout,
    logoutLocal,
    logoutFromAzure,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export UserManager für direkten Zugriff wenn nötig
export { userManager }; 